import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useSolanaTokens } from '../../hooks/useSolanaTokens';

// Solana Brand Colors
const SOL_PURPLE = '#9945FF';
const SOL_GREEN = '#14F195';
const SOL_DARK = '#333333';
const SOL_LIGHT = '#FFFFFF';
const SOL_BLACK = '#000000';

const FooterContainer = styled.div`
  width: 100%;
  background: ${SOL_BLACK};
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding: 12px 24px;
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 100;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.5);
  
  @media (max-width: 768px) {
    padding: 12px 16px;
    flex-direction: column;
    gap: 12px;
  }
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: space-between;
  }
`;

const PriceDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  color: ${SOL_LIGHT};
  font-size: 14px;
`;

const PriceLabel = styled.span`
  color: rgba(255, 255, 255, 0.7);
  font-weight: 500;
`;

const SocialLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const SocialLink = styled.a`
  color: rgba(255, 255, 255, 0.7);
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    color: ${SOL_PURPLE};
    transform: translateY(-1px);
  }
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  position: relative;
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const SearchContainer = styled.div`
  position: relative;
  width: 300px;
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  padding: 8px 16px 8px 36px;
  color: ${SOL_LIGHT};
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  outline: none;
  transition: all 0.2s;
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
  
  &:focus {
    background: rgba(255, 255, 255, 0.15);
    border-color: ${SOL_PURPLE};
    box-shadow: 0 0 0 2px rgba(153, 69, 255, 0.1);
  }
`;

const SearchIconWrapper = styled.div`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(255, 255, 255, 0.5);
  pointer-events: none;
  display: flex;
  align-items: center;
`;

const ExpandedSearchPanel = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  bottom: calc(100% + 12px);
  right: 0;
  width: 400px;
  background: ${SOL_BLACK};
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 16px;
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  flex-direction: column;
  gap: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
  max-height: 400px;
  overflow-y: auto;
  z-index: 90;
  
  @media (max-width: 768px) {
    width: 100%;
    right: 0;
    left: 0;
    bottom: calc(100% + 12px);
  }
`;

const SearchResultItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const TokenLogo = styled.img`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 1px solid rgba(0, 0, 0, 0.1);
`;

const TokenInfo = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const TokenSymbol = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${SOL_LIGHT};
`;

const TokenName = styled.span`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.7);
`;

const SolanaPriceFooter: React.FC = () => {
  const [solPrice, setSolPrice] = useState<number | null>(null);
  const [footerSearch, setFooterSearch] = useState('');

  // Get all Solana tokens for search
  const { tokens: allSolanaTokens } = useSolanaTokens('');

  // Filter tokens for search
  const filteredTokens = useMemo(() => {
    if (!footerSearch || footerSearch.length < 2) return [];
    const search = footerSearch.toLowerCase();
    return allSolanaTokens.filter(token => {
      const mintAddress = (token as any).mint_address || token.contract_address || '';
      return token.name.toLowerCase().includes(search) ||
        token.symbol.toLowerCase().includes(search) ||
        mintAddress.toLowerCase().includes(search);
    }).slice(0, 5);
  }, [allSolanaTokens, footerSearch]);

  // Fetch SOL price from CoinGecko API
  useEffect(() => {
    const fetchSolPrice = async () => {
      try {
        const response = await fetch('https://api.coingecko.com/api/v3/simple/price?ids=solana&vs_currencies=usd');
        if (!response.ok) {
          console.warn('[SolanaPriceFooter] CoinGecko API returned error');
          return;
        }
        const data = await response.json();
        if (data.solana?.usd) {
          setSolPrice(data.solana.usd);
          console.log('[SolanaPriceFooter] SOL price:', data.solana.usd);
        }
      } catch (error) {
        console.error('[SolanaPriceFooter] Failed to fetch SOL price:', error);
      }
    };

    fetchSolPrice();
    const interval = setInterval(fetchSolPrice, 30000);
    return () => clearInterval(interval);
  }, []);

  const formatPrice = (price: number | null): string => {
    if (price === null) return '...';
    return `$${price.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  const handleTokenClick = (token: any) => {
    const mintAddress = (token as any).mint_address || token.contract_address;
    window.location.href = `/solana/token/${mintAddress}`;
  };

  return (
    <FooterContainer>
      <LeftSection>
        <PriceDisplay>
          <TokenLogo src="/landing-assets/Solana.png" alt="SOL" style={{ width: 24, height: 24, borderRadius: '50%' }} />
          <PriceLabel>SOL Price:</PriceLabel>
          <span>{formatPrice(solPrice)}</span>
        </PriceDisplay>
        <SocialLinks>
          <SocialLink href="https://x.com/mavrkofficial" target="_blank" rel="noopener noreferrer">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
            </svg>
          </SocialLink>
          <SocialLink href="https://t.me/mavrkofficialannouncements" target="_blank" rel="noopener noreferrer">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 11.944 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
            </svg>
          </SocialLink>
        </SocialLinks>
      </LeftSection>

      <RightSection>
        <ExpandedSearchPanel $isOpen={footerSearch.length >= 2 && filteredTokens.length > 0}>
          {filteredTokens.map((token) => {
            const mintAddress = (token as any).mint_address || token.contract_address;
            return (
              <SearchResultItem key={mintAddress} onClick={() => handleTokenClick(token)}>
                <TokenLogo src={token.logo_url || '/default-token.png'} alt={token.symbol} />
                <TokenInfo>
                  <TokenSymbol>{token.symbol}</TokenSymbol>
                  <TokenName>{token.name}</TokenName>
                </TokenInfo>
              </SearchResultItem>
            );
          })}
        </ExpandedSearchPanel>
        <SearchContainer>
          <SearchIconWrapper>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
          </SearchIconWrapper>
          <SearchInput
            type="text"
            placeholder="Search tokens..."
            value={footerSearch}
            onChange={(e) => setFooterSearch(e.target.value)}
          />
        </SearchContainer>
      </RightSection>
    </FooterContainer>
  );
};

export default SolanaPriceFooter;

